import React, {useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import ReplayIcon from "@material-ui/icons/Replay";
import {
  Box,
  CircularProgress,
  colors, Divider,
  Drawer,
  IconButton,
  InputAdornment,
  TableRow,
  Typography
} from "@material-ui/core";
import moment from "moment";
import SearchIcon from "@material-ui/icons/Search";
import TuneIcon from "@material-ui/icons/Tune";

import { PageTitle } from "../../../../components/page-title/PageTitle";
import {CustomTooltip} from "../../components/Tooltip";
import DynamicPaginationActions from "../../components/DynamicPaginationActions";
import {InputField} from "../../../../components/input-field/InputField";
import {DefaultButton} from "../../components/Buttons";
import TableData, {SortableCell, StyledTableCell} from "../../components/TableData";
import {NotificationProvider} from "../../../../context/NotificationContext";
import {SortableValue} from "../../components/PersonalAccountFilters";
import ICIPAdminService from "../../../../services/ICIPAdminService";
import Paginator, {DataType, DataTypeEnum} from "../../../../models/Paginator";
import useStyles from "../personal/PersonalTab.style";
import {MemberStatus} from "../../../../settings/MemberStatus";
import {updatePaginator} from "../../../../context/ICIPAdminContext";
import Business from "../../../../models/Business";
import Avatar from "../../components/Avatar";
import {formatPhoneNumber, initials} from "../../../../common/Hooks";
import {Status, StatusType} from "../../components/Status";
import {BusinessTabPanel} from "../../tab-panels/business/BusinessTabPanel";
import BusinessFilters from "../../components/BusinessFilters";
import {FilterState} from "../../../../attributes/filters-state";

enum LoadingStatus {
  loading,
  failure,
  succeed,
  initial,
  adding,
  paginating,
}

interface BusinessTabProps {}

interface BusinessTabData {
  original: Paginator;
  filtered: Paginator;
}

const initialFilters = (): FilterState => {
  return {
    creation_filter: "desc",
    status_filter: MemberStatus.all,
    decline_filter: null,
    approve_filter: null,
  };
};

export const BusinessTab = (props: BusinessTabProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [query, setQuery] = useState("");
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState((): FilterState => initialFilters());
  const [data, setData] = useState((): BusinessTabData => null);
  const [loadingStatus, setLoadingStatus] = useState(
      (): LoadingStatus => LoadingStatus.initial
  );
  const [business, setBusiness] = useState(null);

  const filterData = async (newFilters: FilterState): Promise<boolean> => {
    let result = false;

    // Compute request
    let response = await ICIPAdminService.teamBusinessesMember(null, newFilters);
    if (response.isSuccess && response.data.code === 100) {
      console.dir(response.data.data);
      let paginator = Paginator.fromJson(
          JSON.parse(JSON.stringify(response.data.data)),
          DataTypeEnum.business
      );

      setData({
        original: paginator,
        filtered: paginator,
      });
      setFilters(newFilters);
      result = true;
    }

    return result;
  };

  const fetchBusinesses = (page?: number, useFilters?: FilterState) => {
    setLoadingStatus(LoadingStatus.loading);

    ICIPAdminService.teamBusinessesMember(page, useFilters).then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let paginator = Paginator.fromJson(
            JSON.parse(JSON.stringify(response.data.data)),
            DataTypeEnum.business
        );

        setData({
          original: paginator,
          filtered: paginator,
        });
        setLoadingStatus(LoadingStatus.succeed);
      } else {
        setLoadingStatus(LoadingStatus.failure);
      }
    });
  };

  const refreshDataCallback = (business: Business) => {
    setData({
      original: updatePaginator(data.original, business),
      filtered: updatePaginator(data.filtered, business),
    });
  };

  const sortTableByAttribute = (sortBy: SortableValue, name: string) => {
    let newData: Array<DataType> = data.filtered.items;

    if(name === "created_at") {
      newData = data.filtered.items.sort((a, b) => {
        if(sortBy === "desc") {
          return (a as Business).created_at.getTime() - (b as Business).created_at.getTime();
        }
        return (b as Business).created_at.getTime() - (a as Business).created_at.getTime();
      });
    }

    if(name === "approved_at") {
      newData = data.filtered.items.sort((a, b) => {
        let va = (a as Business).approved_at;
        let vb = (b as Business).approved_at;

        if(va !== null && vb !== null) {
          if(sortBy === "desc") {
            return va.getTime() - vb.getTime();
          }
          return vb.getTime() - va.getTime();
        }

        return 0;
      });
    }

    if(name === "declined_at") {
      newData = data.filtered.items.sort((a, b) => {
        let da = (a as Business).declined_at;
        let db = (b as Business).declined_at;

        if(da !== null && db !== null) {
          if(sortBy === "desc") {
            return da.getTime() - db.getTime();
          }
          return db.getTime() - da.getTime();
        }

        return 0;
      });
    }

    setData({
      ...data,
      filtered: data.filtered.cloneItems(newData),
    });
  };

  const paginatingBusinessesMembers = (page?: number, useFilters?: FilterState) => {
    setLoadingStatus(LoadingStatus.paginating);

    ICIPAdminService.teamBusinessesMember(page, useFilters).then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let paginator = Paginator.fromJson(
            JSON.parse(JSON.stringify(response.data.data)),
            DataTypeEnum.business
        );

        setData({
          original: paginator,
          filtered: paginator,
        });
        setLoadingStatus(LoadingStatus.succeed);
      } else {
        setLoadingStatus(LoadingStatus.failure);
      }
    });
  };

  const errorWrapper = (error?: string): any => {
    return (
        <Box component="div" className={classes.errorWrapper}>
          <Typography component="span">
            {error ? (
                error
            ) : (
                <FormattedMessage id="sharedScreen.unableToLoadData" />
            )}
          </Typography>
          <Box component="div" mt={1}>
            <DefaultButton
                component="div"
                mt={1}
                onClick={(_) => fetchBusinesses(null, initialFilters())}
            >
              <FormattedMessage id="sharedScreen.retry" />
            </DefaultButton>
          </Box>
        </Box>
    );
  };

  const renderBusinessesTableHeader = () => {
    return (
        <TableRow>
          <StyledTableCell align="left">#.</StyledTableCell>
          <StyledTableCell align="left">
            <FormattedMessage id="sharedScreen.businessName" />
          </StyledTableCell>
          <StyledTableCell align="left">
            <FormattedMessage id="sharedScreen.email" />
          </StyledTableCell>
          <StyledTableCell align="left">
            <FormattedMessage id="sharedScreen.telephone" />
          </StyledTableCell>
          <StyledTableCell align="left">
            <FormattedMessage id="sharedScreen.status" />
          </StyledTableCell>
          <StyledTableCell align="left">
            <SortableCell
                ontSort={(sortBy) => sortTableByAttribute(sortBy, "created_at")}
                text="sharedScreen.addedAt"
            />
          </StyledTableCell>
          <StyledTableCell align="left">
            <SortableCell
                ontSort={(sortBy) => sortTableByAttribute(sortBy, "validation_date")}
                text="sharedScreen.approvedAt"
            />
          </StyledTableCell>
          <StyledTableCell align="left">
            <SortableCell
                ontSort={(sortBy) => sortTableByAttribute(sortBy, "declined_at")}
                text="sharedScreen.declinedAt"
            />
          </StyledTableCell>
          <StyledTableCell align="right">
            <FormattedMessage id="sharedScreen.action" />
          </StyledTableCell>
        </TableRow>
    );
  };

  const renderBusinessesTableRows = () => {
    return data.filtered.items
        .filter((item) => {
          let business: Business = item as Business;

          if (query === "") {
            return business;
          } else if (
              business.fullName().toLowerCase().includes(query.toLowerCase())
          ) {
            return business;
          }
        })
        .map((item, index) => {
          let business = item as Business;

          return (
              <TableRow key={index} onClick={(_) => setBusiness(business)}>
                <StyledTableCell scope="row" component="td" align="left">
                  {business.id}
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="left">
                  <Box display="flex" alignItems="center">
                    <Box style={{ marginRight: "14px" }}>
                      <Avatar
                          size={28}
                          src={business.avatar()}
                          alt={initials(business.fullName())}
                      />
                    </Box>
                    {business.fullName()}
                  </Box>
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="left">
                  {business.company_email !== null ? business.company_email : "--"}
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="left">
                  <Typography
                      component="span"
                      style={{ fontWeight: 600, fontSize: "0.86rem" }}
                  >
                    {formatPhoneNumber(business.primary_phone_number)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="left">
                  <Status
                      withBorder
                      status={business.status()}
                      type={StatusType.memberStatus}
                  />
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="left">
                  <Typography
                      component="span"
                      style={{ fontSize: "0.86rem", fontWeight: 600 }}
                  >
                    {business.created_at !== null
                        ? moment
                            .utc(business.created_at)
                            .local()
                            .format("DD, MMM yyyy @HH:mm")
                        : "--"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="left">
                  <Typography
                      component="span"
                      style={{
                        fontSize: "0.86rem",
                        fontWeight: 600,
                        color: colors.green[800],
                      }}
                  >
                    {business.approved_at !== null
                        ? moment
                            .utc(business.approved_at)
                            .local()
                            .format("DD, MMM yyyy @HH:mm")
                        : "--"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="left">
                  <Typography
                      component="span"
                      style={{
                        fontSize: "0.86rem",
                        fontWeight: 600,
                        color: colors.red[800],
                      }}
                  >
                    {business.declined_at !== null
                        ? moment
                            .utc(business.declined_at)
                            .local()
                            .format("DD, MMM yyyy @HH:mm")
                        : "--"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell scope="row" component="td" align="right" />
              </TableRow>
          );
        });
  };

  useEffect(() => {
    fetchBusinesses(null, initialFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.business",
        })}`}
      />
      <Box className={classes.root}>
        <Box component="div" className={classes.header} mb={1}>
          <Box component="div" className={classes.headerWrapper}>
            <Box component="div" mr={1}>
              <Typography variant="h3" component="h3">
                <FormattedMessage id="sharedScreen.allBusinesses" />
              </Typography>
            </Box>
            <Box component="div" className={classes.counter}>
              {loadingStatus === LoadingStatus.succeed || loadingStatus === LoadingStatus.paginating ? (
                  <Typography variant="h3" component="h3">
                    {` ⸱ #${data != null ? data.filtered.total : 0}`}
                  </Typography>
              ) : (
                  <></>
              )}
            </Box>
          </Box>
          {loadingStatus === LoadingStatus.failure ? (
              errorWrapper()
          ) : loadingStatus === LoadingStatus.loading ? (
              <Box component="div" mt={1} className={classes.loadingWrapper}>
                <Box component="div" mr={2} mt={1}>
                  <CircularProgress
                      color="secondary"
                      disableShrink={false}
                      size={20}
                      thickness={4}
                  />
                </Box>
                <Typography component="span" className="caption">
                  <FormattedMessage id="sharedScreen.loadingData" />
                </Typography>
              </Box>
          ) : (
              <></>
          )}
        </Box>
        {loadingStatus === LoadingStatus.succeed || loadingStatus === LoadingStatus.paginating ? (
            <>
              <Box component="div" className={classes.searchHeaderWrapper}>
                <Box component="div" className={classes.paginatorWrapper}>
                  <Box>
                    <CustomTooltip
                        title={intl.formatMessage({
                          id: "sharedScreen.addFilters",
                        })}
                        arrow
                    >
                      <IconButton
                          disabled={false}
                          className={classes.extraAction}
                          onClick={(_) => setOpenFilters(true)}
                      >
                        <TuneIcon fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                  <Box>
                    <CustomTooltip
                        title={intl.formatMessage({ id: "sharedScreen.refresh" })}
                        arrow
                    >
                      <IconButton
                          disabled={false}
                          className={classes.extraAction}
                          onClick={(_) => fetchBusinesses(null, initialFilters())}
                      >
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                  <Divider orientation="vertical" variant="middle" flexItem />
                  <Box ml={2}>
                    <DynamicPaginationActions
                        loading={loadingStatus === LoadingStatus.paginating}
                        enabled={
                          query.length === 0 &&
                          loadingStatus !== LoadingStatus.paginating
                        }
                        pageNumber={data.filtered.page}
                        perPage={data.filtered.per_page}
                        totalPages={data.filtered.total}
                        handlePrevAction={
                          data.filtered.prev_page !== null
                              ? () => {
                                paginatingBusinessesMembers(
                                    data.filtered.page - 1,
                                    filters
                                );
                              }
                              : null
                        }
                        handleNextAction={
                          data.filtered.next_page !== null
                              ? () => {
                                paginatingBusinessesMembers(
                                    data.filtered.page + 1,
                                    filters
                                );
                              }
                              : null
                        }
                    />
                  </Box>
                </Box>
                <Box component="div" className={classes.searchWrapper}>
                  <Box component="div" style={{ width: "400px" }}>
                    <InputField
                        fullWidth
                        id="search"
                        name="search"
                        placeholder={intl.formatMessage({
                          id: "sharedScreen.research",
                        })}
                        type="text"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        endAdornment={
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        }
                    />
                  </Box>
                  <Box component="div" className={classes.actionItem} ml={1}>
                    <DefaultButton aria-label="filters" onClick={(_) => setOpenFilters(true)}>
                      <Box component="div" mr={1}>
                        <TuneIcon fontSize="small" color="inherit" />
                      </Box>
                      <FormattedMessage id="sharedScreen.addFilters" />
                    </DefaultButton>
                  </Box>
                </Box>
              </Box>
              <Box component="div" className={classes.tableWrapper} mb={4} mt={2}>
                <TableData
                    rows={renderBusinessesTableRows()}
                    header={renderBusinessesTableHeader()}
                />
              </Box>
            </>
        ) : (
            <></>
        )}

        <Drawer
            className={classes.dialogDrawer}
            anchor="right"
            open={business != null}
            elevation={0}
            onClose={(_) => setBusiness(null)}
            PaperProps={{
              style: { width: "60%" },
            }}
        >
          {business !== null ? (
              <BusinessTabPanel
                  refreshCallback={refreshDataCallback}
                  business={business}
                  onClose={() => setBusiness(null)}
              />
          ) : (
              <></>
          )}
        </Drawer>

        <Drawer
            className={classes.dialogDrawer}
            anchor="right"
            open={openFilters}
            elevation={0}
            onClose={(_) => setOpenFilters(false)}
            PaperProps={{
              style: { width: "34%" },
            }}
        >
          <NotificationProvider>
            <BusinessFilters
                filters={filters}
                updateFilters={async (filters) => await filterData(filters)}
                onClose={() => setOpenFilters(false)}
            />
          </NotificationProvider>
        </Drawer>
      </Box>
    </>
  );
};
